/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IntlMessagesProvider from '@atlaskit/intl-messages-provider/main';
import defaultMessages from '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/en';

type MessageFile = {
	default: { [key: string]: string };
};

export function getLanguageCode(locale: string): string {
	if (['en-GB', 'en-ZZ', 'pt-PT', 'zh-TW'].includes(locale)) {
		return locale;
	} else if (locale === 'zh-HK') {
		return 'zh-TW';
	}
	return locale.split('-')[0];
}

function fetchLanguageFile(code: string): Promise<MessageFile> {
	switch (code) {
		case 'cs':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-cs" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/cs'
			);
		case 'da':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-da" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/da'
			);
		case 'de':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-de" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/de'
			);
		case 'en-GB':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-en-GB" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/en_GB'
			);
		case 'en-ZZ':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-en-ZZ" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/en_ZZ'
			);
		case 'en':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-en" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/en'
			);
		case 'es':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-es" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/es'
			);
		case 'fi':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-fi" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/fi'
			);
		case 'fr':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-fr" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/fr'
			);
		case 'hu':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-hu" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/hu'
			);
		case 'it':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-it" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/it'
			);
		case 'ja':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-ja" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/ja'
			);
		case 'ko':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-ko" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/ko'
			);
		case 'nb':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-nb" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/nb'
			);
		case 'nl':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-nl" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/nl'
			);
		case 'pl':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-pl" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/pl'
			);
		case 'pt':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-pt_BR" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/pt_BR'
			);
		case 'ru':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-ru" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/ru'
			);
		case 'sv':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-sv" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/sv'
			);
		case 'th':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-th" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/th'
			);
		case 'tr':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-tr" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/tr'
			);
		case 'uk':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-uk" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/uk'
			);
		case 'vi':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-vi" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/vi'
			);
		case 'zh-TW':
		case 'zh-HK':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-zh-TW" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/zh_TW'
			);
		case 'zh':
			return import(
				/* webpackChunkName: "@atlaskit-internal_async-universal-create-i18n-zh-ZH" */ '@atlassian/afm-i18n-platform-navigation-universal-create/i18n/zh'
			);
		default:
			throw new Error(`Unable to find translation for locale ${code}`);
	}
}

export const OPERATIONAL_EVENT_TYPE = 'operational';
export const NAVIGATION_CHANNEL = 'navigation';

function Provider({ children }: { children: ReactNode }) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const loaderFn = async (locale: string) => {
		const intlLocale = new Intl.Locale(locale.replace('_', '-'));
		try {
			const module = await fetchLanguageFile(getLanguageCode(intlLocale.toString()));
			return module.default;
		} catch (e) {
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'not_loaded',
				actionSubject: 'uc_i18n',
				attributes: {
					locale: locale,
				},
			}).fire(NAVIGATION_CHANNEL);
		}
	};
	return (
		<IntlMessagesProvider loaderFn={loaderFn} defaultMessages={defaultMessages}>
			{children}
		</IntlMessagesProvider>
	);
}

export const withIntlProvider =
	<T extends object>(WrappedComponent: React.ComponentType<T>) =>
	(props: T): JSX.Element => (
		<Provider>
			<WrappedComponent {...props} />
		</Provider>
	);
